import {
  networkMap,
  networkDirectory,
  hasProgramAssets,
} from './../networkMap';
import { noSpaces } from './wordFormatting';
import { IMedia, IOffer } from './../models';
import { getRelevantOffer } from './offers';

const getAssetId = ({ assetType, altIds, id }: Partial<IMedia>) => {
  switch (assetType) {
    case 'Series':
      return altIds?.tmsSeriesId;
    case 'Movie':
      return altIds?.tmsRootId;
    case 'Event':
      return id;
    case 'Program':
      return id;
    default:
      return null;
  }
};

const getProviderPath = (networkName: string | undefined, offer: IOffer[]) => {
  if (!networkName && !offer?.length) {
    return '';
  }

  if (networkName) {
    return `${networkName}/`;
  }

  const { providerName, providerId } = getRelevantOffer(offer) || {};

  // Handles LCharter providerName discrepancy
  if (/LCharter/.test(providerName)) {
    let displayName = '';
    switch (providerId) {
      case 'sho.com':
        displayName = 'Showtime';
        break;
      case 'hbo.com':
        displayName = 'HBO';
        break;
      case 'epixhd.com':
        displayName = 'EPIX';
    }
    return displayName ? `${noSpaces(displayName).toLowerCase()}/` : '';
  }

  const targetNetwork =
    providerName && networkMap[networkDirectory[providerName]];

  return targetNetwork
    ? `${noSpaces(targetNetwork.displayName).toLowerCase()}/`
    : '';
};

// extracts path for movie/series page links
export const getLinkPath = (item: IMedia, networkName?: string) => {
  const { assetType, title, offer } = item;
  const assetId = getAssetId(item);
  const formattedTitle = noSpaces(title).toLowerCase();
  const providerPath = getProviderPath(networkName, offer);

  switch (assetType) {
    case 'Series':
      return `/tv/${providerPath}${assetId}/${formattedTitle}/`;
    case 'Movie':
      return `/movies/${assetId}/${formattedTitle}/`;
    case 'Event':
      return `/events/${assetId}/`;
    case 'Program':
      return `/programs/${assetId}/${formattedTitle}`;
    default:
      return '';
  }
};

// links to watch.spectrum asset
export const getExternalLinkPath = (
  media: IMedia,
  visitId: string,
  contextToken: string
) => {
  const { assetType, id, altIds, offer, ovpUrl } = media;

  const { providerId } = getRelevantOffer(offer);

  if (assetType === 'Program' && hasProgramAssets.includes(providerId)) {
    // TODO: better dissect uri string but it looks like the 'lineup and networkid' are constant across many programs
    // return `https://watch.spectrum.net/product/movie/?app=ondemand&uri=%2Fnns%2FV1%2Fevent%2Fproviderassetid%2F${id}%3Fdivision%3DALCO%26vodId%3Dalcoa%26lineup%3D22%26application%3DVOD_PORTAL%26profile%3Dovp_v12%26cacheID%3D159%26networkid%3D235`;
    return `https://watch.spectrum.net/ondemand/play/${id}?visitId=${visitId}${contextToken}`;
  }

  if (ovpUrl && visitId && contextToken) {
    return `${ovpUrl}?visitId=${visitId}${contextToken}`;
  } else if (ovpUrl && visitId) {
    return `${ovpUrl}?visitId=${visitId}`;
  } else if (ovpUrl) {
    return ovpUrl;
  }

  const isMovie = assetType === 'Movie';
  const prefix = isMovie ? 'movie' : 'series';
  const resource = isMovie ? id : `${altIds?.tmsSeriesId}`;
  return `https://watch.spectrum.net/product/${prefix}/${resource}?visitId=${visitId}${contextToken}`;
};
